import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faClipboardCheck, faPlus, faTimes, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../utils/http';

interface CourseRef {
  learnershipId: string;
  title: string;
  saqaId: string;
}

interface Moderator {
  userId: string;
  fullName: string;
  email: string;
  assignedCourses: CourseRef[];
}

/** Admin: assign moderators (read-only reviewers) to courses. */
export default function AdminModeratorsPage() {
  const { accessToken } = useAuth();
  const [moderators, setModerators] = useState<Moderator[]>([]);
  const [courses, setCourses] = useState<CourseRef[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selection, setSelection] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    const [modsRes, coursesRes] = await Promise.all([
      authFetch<Moderator[]>(accessToken, '/api/admin/moderators'),
      authFetch<CourseRef[]>(accessToken, '/api/community/my-courses'),
    ]);
    if (modsRes.ok && modsRes.data) {
      setModerators(modsRes.data);
      setError('');
    } else {
      setError(modsRes.message || 'Failed to load moderators.');
    }
    if (coursesRes.ok && coursesRes.data) setCourses(coursesRes.data);
    setIsLoading(false);
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const assign = async (moderatorUserId: string) => {
    const learnershipId = selection[moderatorUserId];
    if (!learnershipId) return;
    setBusy(moderatorUserId);
    const res = await authFetch(accessToken, '/api/admin/moderators/assignments', {
      method: 'POST',
      body: { moderatorUserId, learnershipId },
    });
    setBusy(null);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setError('');
    setSelection((prev) => ({ ...prev, [moderatorUserId]: '' }));
    load();
  };

  const remove = async (moderatorUserId: string, learnershipId: string) => {
    const res = await authFetch(accessToken, `/api/admin/moderators/assignments/${moderatorUserId}/${learnershipId}`, { method: 'DELETE' });
    if (!res.ok) setError(res.message);
    else load();
  };

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-950">Moderators</h1>
          <p className="text-sm text-brand-900/60 mt-1">Moderators get read-only access to the courses assigned to them</p>
        </div>
        <Link
          to="/admin/users/new"
          className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2 text-sm"
        >
          <FontAwesomeIcon icon={faUserPlus} className="h-3.5 w-3.5" />
          Create moderator
        </Link>
      </div>

      <div className="p-8 space-y-4">
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
          </div>
        ) : moderators.length === 0 ? (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faClipboardCheck} className="h-12 w-12 text-brand-300 mb-4" />
            <p className="text-brand-900/60">No moderator accounts yet. Create one with account type Moderator.</p>
          </div>
        ) : (
          moderators.map((m) => {
            const assignedIds = new Set(m.assignedCourses.map((c) => c.learnershipId));
            const available = courses.filter((c) => !assignedIds.has(c.learnershipId));
            return (
              <div key={m.userId} className="card-surface p-5">
                <p className="font-bold text-brand-950">{m.fullName}</p>
                <p className="text-xs text-brand-900/50">{m.email}</p>

                <div className="flex flex-wrap gap-2 mt-4">
                  {m.assignedCourses.length === 0 && <span className="text-sm text-brand-900/50">No courses assigned.</span>}
                  {m.assignedCourses.map((c) => (
                    <span key={c.learnershipId} className="inline-flex items-center gap-2 pl-3 pr-1.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-semibold">
                      {c.title}
                      <button
                        onClick={() => remove(m.userId, c.learnershipId)}
                        className="p-1 hover:bg-brand-100 rounded-full"
                        title="Remove assignment"
                      >
                        <FontAwesomeIcon icon={faTimes} className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <select
                    value={selection[m.userId] ?? ''}
                    onChange={(e) => setSelection((prev) => ({ ...prev, [m.userId]: e.target.value }))}
                    className="flex-1 px-4 py-2 border border-canvas-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">Assign to a course...</option>
                    {available.map((c) => (
                      <option key={c.learnershipId} value={c.learnershipId}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => assign(m.userId)}
                    disabled={!selection[m.userId] || busy === m.userId}
                    className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={busy === m.userId ? faSpinner : faPlus} className={`h-3.5 w-3.5 ${busy === m.userId ? 'animate-spin' : ''}`} />
                    Assign
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
